// inspired by https://github.com/uidotdev/usehooks/blob/main/index.js#L45
import { isNullish, isZodSchema, notNullish } from "@orh/utils";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import type { z } from "zod/v4";
import { logger } from "../utils/logger";

type Serializer<T> = (object: T) => string;
type Parser<T> = (val: string) => T | undefined;
export type Setter<T> = Dispatch<SetStateAction<T | undefined>>;

type Options<T> = {
  serializer: Serializer<T>;
  parser: Parser<T>;
  logger: (error: unknown) => void;
};

export abstract class StorageAdapter {
  abstract subscribe(key: string): (callback: () => void) => () => void;
  abstract getSnapshot(key: string): string | undefined | null;
  abstract getServerSnapshot(key: string): string | undefined | null;

  abstract removeItem(key: string): void;
  abstract setItem(key: string, value: string): void;
}

const defaultOptions = {
  serializer: JSON.stringify,
  parser: JSON.parse,
  logger: logger.log,
} satisfies Options<unknown>;

/**
 * Universal key value Storage hook that can be used as a drop in replacement for the useState hook.
 * Instead of just keeping the value, it will sync its state with a storage adapter.
 * This storage adapter can implement a more permanent storage and also sync date between browser Tabs.
 *
 * @param storageAdapter Storage Adapter that handles the storing, retrieving and syncing of values.
 * @returns
 */
export const useKvStorage = (storageAdapter: StorageAdapter) => {
  /**
   * Universal key value Storage hook that can be used as a drop in replacement for the useState hook.
   * Instead of just keeping the value, it will sync its state with a storage adapter.
   * This storage adapter can implement a more permanent storage and also sync date between browser Tabs.
   *
   * @param key key under which the value will be stored and retrived by the storage adapter
   * @param initialValue initial value that will be used if the storage under the given key is empty
   * @param userOptions options for customizing the data handling
   * @returns
   */
  function useKvStorage<T>(key: string, initialValue: T, userOptions?: Partial<Options<T>>): [T, Setter<T>];
  /**
   * Universal key value Storage hook that can be used as a drop in replacement for the useState hook.
   * Instead of just keeping the value, it will sync its state with a storage adapter.
   * This storage adapter can implement a more permanent storage and also sync date between browser Tabs.
   *
   * @param key key under which the value will be stored and retrived by the storage adapter
   * @param rawInitialValue initial value that will be used if the storage under the given key is empty
   * @param schema Zod schema for validating the the data to guarantee the shape of the data.
   * Supplying default and or catch values here can be helpful to fix partially corupted data.
   * @param userOptions options for customizing the data handling
   * @returns
   */
  function useKvStorage<T, Schema extends z.ZodType<T>, OutValue extends z.infer<Schema>>(
    key: string,
    rawInitialValue: z.input<Schema> & T,
    schema: Schema,
    userOptions?: Partial<Options<OutValue & T>>,
  ): [OutValue & T, Setter<OutValue & T>];
  function useKvStorage<T, Schema extends z.ZodType<T>>(
    key: string,
    rawInitialValue?: T,
    schemaOrUserOptions?: Schema | Partial<Options<T>>,
    maybeUserOptions?: Partial<Options<T>>,
  ) {
    const { schema, userOptions } = isZodSchema(schemaOrUserOptions)
      ? { schema: schemaOrUserOptions, userOptions: maybeUserOptions }
      : { schema: undefined, userOptions: schemaOrUserOptions };
    const options = useMemo(() => ({ ...defaultOptions, ...userOptions }) as Options<T>, [userOptions]);
    // intentionally a ref as it should only be computed once in the beginning to not cause rerenders
    const initialValueRef = useRef(schema ? schema.safeParse(rawInitialValue).data : rawInitialValue);

    const serializeValue = useCallback(
      (value: T | undefined | null) => {
        if (notNullish(value)) {
          try {
            return options.serializer(value);
          } catch (err) {
            options.logger(err);
            return undefined;
          }
        }
      },
      [options],
    );

    const parseValue = useCallback(
      (value: string | undefined | null) => {
        if (isNullish(value)) {
          if (schema) {
            const result = schema.safeParse(initialValueRef.current);
            return result.data;
          }
          return initialValueRef.current;
        }

        try {
          const parsed = options.parser(value);
          if (schema) {
            const result = schema.safeParse(parsed);
            if (result.success) return result.data;

            options.logger(result.error);
            return undefined;
          }

          return parsed;
        } catch (err) {
          options.logger(err);
          return undefined;
        }
      },
      [options, schema],
    );

    const subscribe = useMemo(() => storageAdapter.subscribe(key), [key]);
    const store = useSyncExternalStore(
      subscribe,
      () => storageAdapter.getSnapshot(key),
      () => storageAdapter.getServerSnapshot(key),
    );

    const setState = useCallback<Setter<T>>(
      v => {
        const nextState = v instanceof Function ? v(parseValue(store)) : v;
        const serializedState = serializeValue(nextState);

        if (isNullish(serializedState)) {
          storageAdapter.removeItem(key);
        } else {
          storageAdapter.setItem(key, serializedState);
        }
      },
      [key, store, parseValue],
    );

    const outValue = useMemo(() => parseValue(store) ?? initialValueRef.current, [parseValue, store]);
    return [outValue, setState];
  }

  return useKvStorage;
};
